import { useCallback } from "react";

import {
  AgGridEvent,
  ColumnPinnedEvent,
  ColumnResizedEvent,
  ColumnState,
  ColumnVisibleEvent,
  DragStoppedEvent,
  GridApi,
} from "@ag-grid-community/core";
import debounce from "lodash-es/debounce";

const callRedrawRows = (api: GridApi) => {
  api.redrawRows();
};

const debouncedRedrawRows = debounce(callRedrawRows, 100, {
  maxWait: 500,
  trailing: true,
});

type Parameters = {
  onColumnModelChange?: (columnState: ColumnState[]) => void;
  onColumnPinned?: (event: ColumnPinnedEvent) => void;
  onColumnResized?: (event: ColumnResizedEvent) => void;
  onColumnVisible?: (event: ColumnVisibleEvent) => void;
  onDragStopped?: (event: DragStoppedEvent) => void;
  syncColumnToolPanel?: () => void;
};

const useColumnModelChangeListeners = ({
  onColumnModelChange,
  onColumnPinned,
  onColumnResized,
  onColumnVisible,
  onDragStopped,
  syncColumnToolPanel,
}: Parameters) => {
  // Here's a bunch of handlers which listen to all column model changes:
  // moving, resizing, pinning, etc. Why not using the "displayedColumnsChanged" event
  // for everything? First of all it doesn't fire on individual column resizing,
  // second it fires on every column movement. It's more optimal to use dragStopped
  // instead, to only fire onColumnModelChange when the column movement is finished.

  const handleColumnModelChange = useCallback(
    (event: AgGridEvent) => {
      onColumnModelChange?.(event.columnApi.getColumnState());
      syncColumnToolPanel?.();
    },
    [onColumnModelChange, syncColumnToolPanel]
  );

  const handleColumnPinned = useCallback(
    (event: ColumnPinnedEvent) => {
      handleColumnModelChange(event);
      onColumnPinned?.(event);
      syncColumnToolPanel?.();
    },
    [handleColumnModelChange, onColumnPinned, syncColumnToolPanel]
  );
  const handleColumnResized = useCallback(
    (event: ColumnResizedEvent) => {
      // redraw rows to recalculate their height
      if (event.column?.getColDef().autoHeight) {
        debouncedRedrawRows(event.api);
      }
      handleColumnModelChange(event);
      onColumnResized?.(event);
      syncColumnToolPanel?.();
    },
    [handleColumnModelChange, onColumnResized, syncColumnToolPanel]
  );
  const handleColumnVisible = useCallback(
    (event: ColumnVisibleEvent) => {
      handleColumnModelChange(event);
      onColumnVisible?.(event);
      syncColumnToolPanel?.();
    },
    [handleColumnModelChange, onColumnVisible, syncColumnToolPanel]
  );
  const handleDragStopped = useCallback(
    (event: DragStoppedEvent) => {
      handleColumnModelChange(event);
      onDragStopped?.(event);
      syncColumnToolPanel?.();
    },
    [handleColumnModelChange, onDragStopped, syncColumnToolPanel]
  );

  return {
    handleColumnPinned,
    handleColumnResized,
    handleColumnVisible,
    handleDragStopped,
  };
};

export default useColumnModelChangeListeners;
